import { Injectable, inject } from '@angular/core';
import { Observable, combineLatest, map } from 'rxjs';
import { PurchaseOrderService } from './purchase-order.service';
import { ExpenseService } from './expense.service';
import { PaymentService } from './payment.service';
import { VendorService } from './vendor.service';
import { Vendor } from '../models/vendor.model';
import { appliedReturnCredits } from '../models/purchase-order.model';
import { addDays, agingBucket } from '../utils/aging.util';

export type { AgingBucket } from '../utils/aging.util';
export { agingBucket } from '../utils/aging.util';

export type ApSourceType = 'purchase-order' | 'expense';
export type ApPaymentStatus = 'Paid' | 'Partially Paid' | 'Unpaid' | 'Internal';

export interface ApLedgerRow {
  id: string;
  sourceType: ApSourceType;
  number: string;
  vendorId: string;
  vendorName: string;
  category: string;
  invoiceNumber?: string;
  requestedDate: string;
  dueDate: string;
  receivedDate?: string;
  totalAmount: number;
  paidAmount: number;
  /** Applied return-to-shipper credits (purchase orders). */
  creditAmount?: number;
  remainingBalance: number;
  status: ApPaymentStatus;
  /** Whether this row counts toward Accounts Payable at all (POs always; Expenses only if addToAp). */
  isPayable: boolean;
  connectedPurchaseOrderId?: string;
}

/**
 * Unifies Purchase Orders and Expenses into a single Accounts Payable
 * ledger, with remaining balances derived from applied Payments — nothing
 * here is stored redundantly, so the ledger can never drift out of sync
 * with the underlying orders/expenses/payments.
 */
@Injectable({ providedIn: 'root' })
export class AccountsPayableService {
  private readonly purchaseOrderService = inject(PurchaseOrderService);
  private readonly expenseService = inject(ExpenseService);
  private readonly paymentService = inject(PaymentService);
  private readonly vendorService = inject(VendorService);

  getLedger(): Observable<ApLedgerRow[]> {
    return combineLatest([
      this.purchaseOrderService.getAll(),
      this.expenseService.getAll(),
      this.paymentService.getAll(),
      this.vendorService.getAll(),
    ]).pipe(
      map(([purchaseOrders, expenses, payments, vendors]) => {
        const vendorsById = new Map<string, Vendor>(vendors.map((v) => [v.id, v]));
        const paidByPoId = new Map<string, number>();
        const paidByExpenseId = new Map<string, number>();

        for (const payment of payments) {
          for (const app of payment.applications) {
            const map = app.type === 'purchase-order' ? paidByPoId : paidByExpenseId;
            map.set(app.id, (map.get(app.id) ?? 0) + app.amountApplied);
          }
        }

        const rows: ApLedgerRow[] = [];

        for (const po of purchaseOrders) {
          if (po.status === 'Cancelled') continue;
          const vendor = vendorsById.get(po.vendorId);
          const paidAmount = paidByPoId.get(po.id) ?? 0;
          // Applied return-to-shipper credits lower what is owed to the vendor.
          const creditAmount = appliedReturnCredits(po);
          const remainingBalance = Math.max(0, po.total - paidAmount - creditAmount);
          rows.push({
            id: po.id,
            sourceType: 'purchase-order',
            number: po.number,
            vendorId: po.vendorId,
            vendorName: vendor?.name ?? 'Unknown vendor',
            category: 'Purchase Order',
            invoiceNumber: po.vendorInvoiceNumber,
            // The PO's requested (delivery) date; the order date when it has none.
            requestedDate: po.expectedDate ?? po.orderDate,
            // Vendor terms (Net D) run from the requested date.
            dueDate: addDays(po.expectedDate ?? po.orderDate, vendor?.netDays ?? 0),
            receivedDate: po.receivedDate,
            totalAmount: po.total,
            paidAmount,
            remainingBalance,
            status: remainingBalance <= 0 ? 'Paid' : paidAmount + creditAmount > 0 ? 'Partially Paid' : 'Unpaid',
            isPayable: true,
            creditAmount,
          });
        }

        for (const expense of expenses) {
          const vendor = vendorsById.get(expense.vendorId);
          const paidAmount = paidByExpenseId.get(expense.id) ?? 0;
          const remainingBalance = expense.addToAp ? Math.max(0, expense.amount - paidAmount) : 0;
          rows.push({
            id: expense.id,
            sourceType: 'expense',
            number: expense.number,
            vendorId: expense.vendorId,
            vendorName: vendor?.name ?? 'Unknown vendor',
            category: expense.category,
            invoiceNumber: expense.invoiceNumber,
            requestedDate: expense.requestedDate,
            dueDate: expense.dueDate ?? addDays(expense.requestedDate, vendor?.netDays ?? 0),
            totalAmount: expense.amount,
            paidAmount,
            remainingBalance,
            status: !expense.addToAp
              ? 'Internal'
              : remainingBalance <= 0
                ? 'Paid'
                : paidAmount > 0
                  ? 'Partially Paid'
                  : 'Unpaid',
            isPayable: expense.addToAp,
            connectedPurchaseOrderId: expense.connectedPurchaseOrderId,
          });
        }

        return rows.sort((a, b) => b.requestedDate.localeCompare(a.requestedDate));
      }),
    );
  }
}
